import {
  Box,
  Flex,
  FlexProps,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { FC, useRef } from 'react';

import NotificationIcon from '@/assets/icons/ic-notification.svg';
import PeopleIcon from '@/assets/icons/ic-people.svg';

import { PopoverButton } from '@/components/ui/core/PopoverButton/PopoverButton';
import { ChatPopup } from '@/components/ui/shared/chat/ChatPopup/ChatPopup';
import { NotificationBadge } from '@/components/ui/shared/notifications/NotificationBadge/NotificationBadge';
import { Notifications } from '@/components/ui/shared/notifications/Notifications/Notifications';
import { ErrorFallback } from '@/components/ui/shared/overlays/ErrorFallback/ErrorFallback';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { ResourceType } from '@/enums/ResourceType';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { eventConfigurationQueryOptions } from '@/queries/upgrade/eventConfiguration';
import { BELL_RESOURCE_TYPES } from '@/queries/upgrade/notifications';

// const DynamicDirectMessaging = dynamic(
//   () =>
//     import('@/components/ui/shared/direct-messaging/DirectMessaging/DirectMessaging').then(
//       (mod) => mod.DirectMessaging
//     ),
//   {
//     ssr: false,
//     loading: () => (
//       <Flex alignItems="center" justifyContent="center" h="full">
//         <CircleLoader size="lg" color="gray.200" />
//       </Flex>
//     ),
//   }
// );

export const SecondaryMenuDesktop: FC<FlexProps> = (props) => {
  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: eventConfiguration } = useQuery(eventConfigurationQueryOptions(eventId));

  const chat = useDisclosure();
  // wraps the trigger and the (unportalled) content, so a click on either isn't outside
  const chatRef = useRef<HTMLDivElement>(null);

  // Closes on a click outside rather than on blur. The chat is full of controls that disappear or get
  // disabled right after they are clicked - send turns into a spinner, a deleted message takes its menu
  // with it - and the browser then blurs them with nowhere for focus to go, which blur-closing reads
  // as focus leaving the popup.
  useOutsideClick({ ref: chatRef, enabled: chat.isOpen, handler: chat.onClose });

  return (
    <Flex {...props}>
      <HStack spacing={0}>
        {eventConfiguration?.direct_messaging && (
          // display: contents keeps the wrapper out of the header's layout while still containing the popover
          <Box ref={chatRef} display="contents">
            <Popover
              variant="nav"
              isLazy
              placement="bottom-end"
              isOpen={chat.isOpen}
              onOpen={chat.onOpen}
              onClose={chat.onClose}
              closeOnBlur={false}
            >
              <PopoverTrigger>
                <PopoverButton variant="nav" aria-label="Open direct messages">
                  <NotificationBadge refreshInterval={30_000} resourceType={ResourceType.EventChatChannel}>
                    <PeopleIcon />
                  </NotificationBadge>
                </PopoverButton>
              </PopoverTrigger>
              <PopoverContent h="calc(100vh - 80px)" maxH="800px">
                <PopoverBody>
                  <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
                    <ChatPopup />
                  </BugsnagErrorBoundary>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>
        )}

        <Popover variant="nav" isLazy placement="bottom-end">
          <PopoverTrigger>
            <PopoverButton variant="nav" aria-label="Open notifications">
              <NotificationBadge resourceType={BELL_RESOURCE_TYPES}>
                <NotificationIcon />
              </NotificationBadge>
            </PopoverButton>
          </PopoverTrigger>
          <PopoverContent h="calc(100vh - 80px)" maxH="800px">
            <PopoverBody>
              <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
                <Notifications />
              </BugsnagErrorBoundary>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    </Flex>
  );
};
